/**
 * CS2 map coordinate transforms.
 * Values from game overview .txt files (pos_x, pos_y, scale).
 *
 * gameToPixel converts in-game world coordinates to pixel coordinates
 * on the radar image (1024x1024 default).
 */

const MAP_TRANSFORMS = {
  // Official maps
  de_ancient: { pos_x: -2953, pos_y: 2164, scale: 5.0 },
  de_mirage:  { pos_x: -3230, pos_y: 1713, scale: 5.0 },
  de_inferno: { pos_x: -2087, pos_y: 3870, scale: 4.9 },
  de_nuke:    { pos_x: -3453, pos_y: 2887, scale: 7.0 },
  de_anubis:  { pos_x: -2796, pos_y: 3328, scale: 5.22 },
  de_dust2:   { pos_x: -2476, pos_y: 3239, scale: 4.4 },
  de_overpass: { pos_x: -4831, pos_y: 1781, scale: 5.2 },
  de_train:   { pos_x: -2308, pos_y: 2078, scale: 4.082077 },
  de_cache:   { pos_x: -2000, pos_y: 3250, scale: 5.5 },
  de_vertigo: { pos_x: -3168, pos_y: 1762, scale: 4.0 },
  // Workshop maps (key = workshop ID / DB tag)
  3073892687: { pos_x: -1003, pos_y: 2521, scale: 5.00 },           // Season
  3117367319: { pos_x: -2476, pos_y: 1239, scale: 4.4 },            // Dust 2 Mirror
  3246527710: { pos_x: -4395.903, pos_y: 4203.903, scale: 2.1756864 }, // Grail
  3145028641: { pos_x: -3453, pos_y: 4287, scale: 7 },              // Nuke Mirror
  3075706807: { pos_x: -2129, pos_y: 2368, scale: 5.00 },           // Biome
  3113178445: { pos_x: -2160, pos_y: 2980, scale: 5.28 },           // Nache
  3110463075: { pos_x: -2087, pos_y: 1150, scale: 4.9 },             // Inferno Mirror
  3127729110: { pos_x: -2916, pos_y: 4131, scale: 6.11 },           // Dust
  3267671493: { pos_x: -5141, pos_y: 1088, scale: 4.85 },           // Tuscan
  3270832263: { pos_x: -2438, pos_y: 3690, scale: 5.0 },            // Sub-Zero
  3329387648: { pos_x: -3840, pos_y: 3072, scale: 6.0 },            // Cobbelstone 2018
  de_santo:   { pos_x: -1042.7368, pos_y: 1842.4167, scale: 4.508577 }, // Santo
  // Basalt + Dust 2 Minecraft: värden ur kartornas egna resource/overviews/*.txt i workshop-VPK:n,
  // verifierade 2026-10-19 mot nav-meshen (99,7 % / 100 % av nav-areornas mittpunkter hamnar på
  // radarbildens spelbara yta). Minecraft hade tidigare pos_x -627.44 / pos_y 1715.44, som bara
  // träffade 57 % — heatmapens punkter låg förskjutna. Båda nycklarna behövs: positionsdata använder
  // det interna namnet, matchstatistiken ibland workshop-id:t.
  de_basalt:  { pos_x: -2345.6, pos_y: 2391.8, scale: 4.37 },      // Basalt
  3329258290: { pos_x: -2345.6, pos_y: 2391.8, scale: 4.37 },      // Basalt
  de_minecraft_dust2: { pos_x: -313.4405, pos_y: 1467.4409, scale: 4.7020326 }, // Dust 2 Minecraft
  3664545885: { pos_x: -313.4405, pos_y: 1467.4409, scale: 4.7020326 },         // Dust 2 Minecraft
  de_brewery: { pos_x: -4122.4, pos_y: 4394.4, scale: 2.1820312 },            // Brewery
  de_dogtown: { pos_x: -1741.0228, pos_y: 3295.1, scale: 2.0323243 },         // Dogtown
  de_edin:    { pos_x: -383.30038, pos_y: 4420.2134, scale: 4.803717 },        // Edin
  de_jura:    { pos_x: -2126.9092, pos_y: 2389.8, scale: 2.504188 },           // Jura
  de_palais:  { pos_x: -1353.2374, pos_y: 2044.8105, scale: 2.8003397 },       // Palais
  de_whistle: { pos_x: -1825.6, pos_y: 1104.82, scale: 2.8 },                 // Whistle
};

/**
 * Convert CS2 world coordinates to pixel coordinates on the radar image.
 *
 * @param {number} x - World X coordinate
 * @param {number} y - World Y coordinate
 * @param {string} mapName - Map name (e.g. "de_ancient")
 * @param {number} imageSize - Radar image size in pixels (default 1024)
 * @returns {[number, number]} [px, py] pixel coordinates
 */
export function gameToPixel(x, y, mapName, imageSize = 1024) {
  const t = MAP_TRANSFORMS[mapName];
  if (!t) return [0, 0];

  const px = (x - t.pos_x) / t.scale;
  const py = (t.pos_y - y) / t.scale;

  // Scale to actual image size (overview files assume 1024)
  const factor = imageSize / 1024;
  return [px * factor, py * factor];
}

/**
 * Check if a map has radar transform data available.
 */
export function hasMapTransform(mapName) {
  return mapName in MAP_TRANSFORMS;
}

/**
 * Get the radar image URL for a map.
 * Files served from /maps/{mapName}_radar.png via nginx volume mount.
 * Radar images must exist in cs2-battle-stack/map_images/ (copied from frontend/public/maps/).
 */
export function getRadarImageUrl(mapName) {
  if (mapName in MAP_TRANSFORMS) {
    // ?v=2: cache-buster, same reason as getMapImageUrl in formatters.js —
    // missing files were once cached as index.html with 7d immutable headers.
    return `${process.env.PUBLIC_URL || ''}/images/maps/${mapName}_radar.png?v=2`;
  }
  return null;
}

export default MAP_TRANSFORMS;
